import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../stories/Buttons/Button";
import useApis from "../../services/useAPI";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { Spinner } from "react-bootstrap";
import { accessError } from "../../utils/constants";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { useMediaQuery } from "react-responsive";

/**
 * ShConsent component
 *
 * This component displays information about the appropriate testing windows for
 * sexually transmitted infections (STIs) after exposure. It provides guidance
 * on when to test and encourages users to seek help if necessary.
 *
 * The user can navigate to the next step by clicking the "Confirm & Continue" button.
 *
 * @returns {JSX.Element} The rendered consent information and navigation button.
 */

export default function ShConsent() {
  const { primaryBarcode } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const barcodeValue = primaryBarcode?.primaryBarcode || primaryBarcode;
  const typeUrl = `/api/dashboard/check-test/${barcodeValue}/`;
  const { callApi, serverError, resultGet } = useApis();
  const location = useLocation()
  const auth = useAuth()
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const handleSubmit = () => {
    const postPayload = {
      code: resultGet.primary_barcode_id.toUpperCase(),
      activation_stage: "sh-consent",
    };
    callApi(postPayload, "/api/dashboard/activating/", "POST");
    setError(serverError);
    const newProgressSteps = updateProgressAfterCompletion(resultGet, resultGet?.type, location.pathname);
    auth?.setProgressSteps(newProgressSteps);
    navigate(`/time-administered/${primaryBarcode}`);
  };

  useEffect(() => {
    if (barcodeValue) {
      setIsLoading(true);
      callApi({}, typeUrl, "GET");
    }
  }, [barcodeValue, typeUrl]);


  useEffect(() => {
    if (resultGet?.activation_stage === null) {
      setError(accessError);
    }
    const newProgressSteps = calculateProgressSteps(resultGet, resultGet?.type, location.pathname);
    auth?.setProgressSteps(newProgressSteps);
    if (resultGet?.activation_stage) {
      // Check if activation_stage is one of the allowed values
      const allowedStages = ["personal-details", "collect-sample", "sh-consent"];

      if (resultGet.activation_stage && !allowedStages.includes(resultGet.activation_stage)) {
        setError(accessError);
      } else if (allowedStages.includes(resultGet.activation_stage)) {
        setIsLoading(false);
      } else {
        setError("");
      }
    }
  }, [resultGet]);

  useEffect(() => {
    setError(error || serverError);
  }, [serverError, error])

  const handlePrevious = () => {
    navigate(-1);
  };

  if (error) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border"  className="my-custom-spinner"/>
      </div>
    );
  }

  return (
    <div className="row justify-content-center sh-consent-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth?.progressSteps?.totalSteps}
          currentStep={auth?.progressSteps?.currentStep}
          completedSteps={auth?.progressSteps?.completedSteps}
          isCompleted={auth?.progressSteps?.completedSteps}
          is_assessment_completed={false}
        />
        <h1 className="my-4">When should I test after exposure?</h1>

        <p>
          {" "}
          When testing for sexually transmitted infections (STIs), some
          infections do not show up immediately after sex. The testing window,
          or the period of time after exposure you will get an accurate result,
          can vary by infection type. For the most accurate test results, we
          recommend that you follow the testing windows listed below.
        </p>
        <p>
          {" "}
          <strong>Chlamydia</strong> - 2 weeks after exposure
        </p>
        <p>
          {" "}
          <strong>Gonorrhoea</strong> - 2 weeks after exposure
        </p>
        <p>
          {" "}
          <strong>Syphilis*</strong> - 4 weeks after exposure
        </p>
        <p>
          {" "}
          <strong>HIV*</strong> - 45 days after exposure. This applies to
          fourth-generation testing, which is the testing method used at our
          lab.
        </p>
        <p>
          {" "}
          If you've had high-risk exposure to either syphilis or HIV, we
          recommend seeking help as soon as possible by attending a local sexual
          health clinic or speaking to your GP. If you have been in contact with
          HIV, you may need post-exposure prophylaxis after sexual intercourse.
          Both tests should be repeated 12 weeks after your exposure.
        </p>

        <p>
          <span className="text-rusty-red">
            *If you are unsure about whether you are within a testing window,
            you should do a test now and another test in 12 weeks.
          </span>
        </p>
        <div className={`my-4 mx-auto row d-flex justify-content-between g-2 ${isMobile && "flex-column-reverse"}`}>
      <button
        type="button"
        onClick={handlePrevious}
        className="btn btn-white col-12 col-md-5 col-lg-4 col-xl-3 me-3 btn-layout ageo-semibold"
      >
        Previous step
      </button>
          <Button label={"Confirm & Continue"} onClick={handleSubmit} className="btn btn-primary col-12 col-md-5 col-lg-4 col-xl-3 btn-layout ageo-semibold"></Button>
        </div>
      </div>
    </div>
  );
}
